import { createContext, useContext, useMemo } from 'react';
import {
  DEFAULT_COMPANY_FORMAT,
  formatDate,
  money,
  percent,
  quantity,
  type CompanyFormat,
  type DateOptions,
  type MoneyOptions,
  type NegativeStyle,
  type PercentOptions,
  type QuantityOptions,
} from '@workbook/shared';

/**
 * 🔴 THE COMPANY'S NUMBER AND DATE FORMAT, AS A CONTEXT — design-system plan DS-7a (§2).
 *
 * PURE ON PURPOSE: no `api/client`, no `auth/session`, no `modules/query`. The portal and the console borrow the kit
 * (`check-portal-bundle.mjs`, `check-system-bundle.mjs`), and a context that fetched its own data would drag the back
 * office's session into both. Each shell mounts its own provider with the value it already holds — the back office
 * from `/me/memberships`, the till from the device context, the portal from the partner context — and the console
 * mounts none.
 *
 * ⚠️ WITH NO PROVIDER THE ANSWER IS `DEFAULT_COMPANY_FORMAT`, never `undefined`: BDT, Asia/Dhaka, lakh. A component
 * rendered outside every shell (a spec, the showcase) still prints `৳1,05,000.00`.
 */
export const CompanyFormatContext = createContext<CompanyFormat>(DEFAULT_COMPANY_FORMAT);

/**
 * 🔴 O-21: A MINUS ON SCREENS, BRACKETS ON STATEMENTS AND REPORTS.
 *
 * `ReportPage` and the portal statement provide `'parens'`; everything else inherits the default `'minus'`. A `Money`
 * with its own `negative` prop wins over the context.
 */
export const NegativeStyleContext = createContext<NegativeStyle>('minus');

/** The shared formatters with the company's format already bound — for strings a component cannot hold. */
export interface BoundFormatters {
  money: (value: string, opts?: Omit<MoneyOptions, 'format'>) => string;
  quantity: (value: string | number, opts?: Omit<QuantityOptions, 'format'>) => string;
  percent: (value: string | number, opts?: PercentOptions) => string;
  date: (value: string | Date | null | undefined, opts?: Omit<DateOptions, 'format'>) => string;
}

/**
 * The company format and the four formatters bound to it: for a toast, a `title`, an `aria-label`, an `<option>` —
 * text that is a string, not an element, and so cannot be a `<Money>`.
 */
export function useCompanyFormat(): CompanyFormat & BoundFormatters {
  const format = useContext(CompanyFormatContext);
  return useMemo(
    () => ({
      ...format,
      money: (value, opts) => money(value, { ...opts, format }),
      quantity: (value, opts) => quantity(value, { ...opts, format }),
      percent: (value, opts) => percent(value, opts),
      date: (value, opts) => formatDate(value, { ...opts, format }),
    }),
    [format],
  );
}
